import { useState } from 'react';

import { ClaimsTable, HistoryTable, PositionsTable, StatusList, TransfersTable } from '../components/Tables.tsx';
import { PnlChart } from '../components/PnlChart.tsx';
import { Card, compactUsd, Row, Tabs, tone } from '../components/ui.tsx';
import { deployment } from '../config.ts';
import { fmtUnits, fmtUsd } from '../lib/format.ts';
import { closedNet, viewPosition } from '../lib/positions.ts';
import { useApp } from '../state.tsx';

export function PortfolioPage() {
  const { user, positions, markets, globals, account, wallet, block, openConnect } = useApp();
  const [tab, setTab] = useState<'open' | 'history' | 'transfers' | 'queue' | 'activity'>('open');
  const views = (positions ?? []).map((p) => viewPosition(p, markets, globals));
  const openPnl = views.reduce((a, v) => a + (v.net ?? 0n), 0n);
  const openNotional = views.reduce((a, v) => a + v.notional, 0n);
  const inUse = views.reduce((a, v) => a + v.p.margin, 0n);
  const history = account?.history ?? [];
  const realized = history.reduce((a, h) => a + closedNet(h, globals), 0n);
  const volume = history.reduce((a, h) => a + h.margin * h.leverage, 0n) + openNotional;
  const equity = user ? user.balance + inUse + openPnl : undefined;
  const dash = '- -';

  return (
    <div className="dash">
      <div className="dash-cards">
        <div className="dash-col">
          <Card title="Open Positions">
            <Row label="Open PnL" tone={tone(openPnl)}>
              {user ? compactUsd(openPnl) : dash}
            </Row>
            <Row label="Open Notional">{user ? compactUsd(openNotional) : dash}</Row>
            <Row label="Margin in Use">{user ? compactUsd(inUse) : dash}</Row>
          </Card>
          <Card title="Papurr">
            <Row label="Papurr Earned">{account ? fmtUnits(account.papurrEarned, 18, 2) : dash}</Row>
            <Row label="Papurr Balance">{user ? fmtUnits(user.papurr, 18, 2) : dash}</Row>
            <Row label="Papurr Staked">{user ? fmtUnits(user.staked, 18, 2) : dash}</Row>
            <Row label="Claimable Rewards" tone="pos">
              {/* the same figure as the Papurr page: the stream plus the burn auction's closed epochs */}
              {user ? `${fmtUnits(user.pendingReward + (deployment.burnAuction ? user.auctionClaimable : 0n), 8, 4)} HYPE` : dash}
            </Row>
            <Row label="Rewards Claimed">{account ? `${fmtUnits(account.rewardsClaimed, 8, 4)} HYPE` : dash}</Row>
          </Card>
        </div>
        <Card title="Stats">
          <Row label="Equity">{equity !== undefined ? compactUsd(equity) : dash}</Row>
          <Row label="Available to Trade">{user ? `$${fmtUsd(user.balance)}` : dash}</Row>
          <Row label="Historical PnL" tone={tone(realized)}>
            {account ? compactUsd(realized) : dash}
          </Row>
          <Row label="Volume">{account ? compactUsd(volume) : dash}</Row>
          <Row label="Trades">{account ? history.length + views.length : dash}</Row>
        </Card>
        <Card title="Realized PnL" className="dash-chart">
          {!wallet ? (
            <div className="empty">
              <button className="btn btn-primary btn-sm" onClick={() => openConnect(true)}>
                Connect Wallet
              </button>
            </div>
          ) : (
            <PnlChart history={history} globals={globals} block={block} />
          )}
        </Card>
      </div>
      <section className="panel">
        <Tabs
          tabs={[
            { id: 'open', label: 'Open Positions', count: positions?.length },
            { id: 'history', label: 'Trade History' },
            { id: 'transfers', label: 'Deposits / Withdrawals' },
            { id: 'queue', label: 'Open Queue Balances', count: account?.claims.length },
            { id: 'activity', label: 'Activity' },
          ]}
          value={tab}
          onChange={setTab}
        />
        {tab === 'open' ? <PositionsTable /> : tab === 'history' ? <HistoryTable rows={history} /> : tab === 'transfers' ? <TransfersTable /> : tab === 'queue' ? <ClaimsTable /> : <StatusList />}
      </section>
    </div>
  );
}
